import { getPOIFrom } from '../../utils';
import eventBus from '../../utils/emitter-event-bus';
import { NavigatorEventBus, POI_ID } from '../navigator/navigator.model';
import { WorldEventBus } from '../world/world.model';
import { Direction } from './joystick.model';

/** Service responsible for showing a joystick on map press */
export class JoystickIntegrated {
  private static instance: JoystickIntegrated;
  private navigatorElement = <HTMLElement>document.querySelector('.js-navigator');
  private joystickElement = <HTMLElement>document.querySelector('.js-joystick-integrated');
  private activeClass = 'is-active';

  private backdropElement = <HTMLElement | null>document.querySelector('.device');
  private screenElement = <HTMLElement | null>document.querySelector('.device__screen');
  private heroElement = <HTMLElement | null>document.querySelector('.hero');

  private directions: Direction[] = [];
  private hasNavigated = false;
  private isMousePressed = false;
  private lastX = 0;
  private lastY = 0;
  private lastMoveEvent: MouseEvent | TouchEvent | null = null;
  private directionThreshold = 20;

  /** Horseback (future feature): when true, holding the joystick toward an exit
   * keeps moving the player POI-by-POI without releasing. Off by default, so a
   * move commits once and the joystick disappears the moment it does; the player
   * must release and press again to move again — but they can do so straight
   * away, without waiting for the hero to arrive. */
  private continuousMovement = false;

  /** The current press's live drag listeners, stored so they can be detached
   * reliably from any teardown site (release, navigation end). */
  private activeMoveHandler: ((event: MouseEvent | TouchEvent) => void) | null = null;
  private activeEndHandler: (() => void) | null = null;

  /** While the knob is held at the rim toward a valid exit, the move "charges"
   * for this long before it commits; the ring fill is animated over the same
   * duration in CSS (.is-charging). Keep the two in sync. */
  private chargingClass = 'is-charging';
  private chargeDurationMs = 1000;
  private chargeTimer: ReturnType<typeof setTimeout> | null = null;
  private chargingDestination: POI_ID | null = null;

  constructor() {
    this.initializeEventListeners();
  }

  static getInstance(): JoystickIntegrated {
    if (!JoystickIntegrated.instance) {
      JoystickIntegrated.instance = new JoystickIntegrated();
    }
    return JoystickIntegrated.instance;
  }

  private initializeEventListeners(): void {
    this.handleJoystickNavigation();
    this.handleBackdropPress();
    eventBus.on(WorldEventBus.initialise, (poi) => this.setAvailableDirections(poi));
    eventBus.on(NavigatorEventBus.navigationEnd, (poi) => this.onNavigationEnd(poi));
  }

  /**
   * Backdrop (desktop only): a press on the dark area around the phone frame
   * starts the joystick exactly as a press on the map does, so the whole window
   * is a valid place to begin a drag. Presses on the map are left to the
   * navigator's own listener, and presses on in-phone UI (buttons, etc.) are
   * left alone — only true backdrop presses start a drag here.
   */
  private handleBackdropPress(): void {
    const { backdropElement } = this;
    if (!backdropElement) return;

    const startFromBackdrop = (event: MouseEvent | TouchEvent): void => {
      const target = event.target as Node | null;
      // Map presses bubble up here too; the navigator starts those itself.
      if (target && this.navigatorElement.contains(target)) return;
      // Leave in-phone UI (narrator, merchant, inventory…) its own clicks.
      if (this.isInsideScreen(event)) return;
      this.startEvent(event);
    };

    backdropElement.addEventListener('mousedown', startFromBackdrop);
    backdropElement.addEventListener('touchstart', startFromBackdrop);
  }

  /** True when a pointer event lands within the phone screen's box. */
  private isInsideScreen(event: MouseEvent | TouchEvent): boolean {
    if (!this.screenElement) return false;
    const rect = this.screenElement.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  }

  private handleJoystickNavigation(): void {
    this.navigatorElement.addEventListener('mousedown', this.startEvent.bind(this));
    this.navigatorElement.addEventListener('touchstart', this.startEvent.bind(this));
  }

  private startEvent(event: MouseEvent | TouchEvent): void {
    // Defensive: drop any listeners left over from a press that didn't clean up.
    this.removeInteractionListeners();
    this.isMousePressed = true;
    const { x, y, rect } = this.getEventCoordinates(event);
    // The joystick is drawn centred on the player's on-screen position (the hero
    // dot), not the press point, so the knob and charge ring stay visible rather
    // than hidden under the finger. The press point is still the drag origin
    // (see handleMoveEvent), so the knob tracks the drag direction and distance.
    // Reset the hero to its true centre first so the circle is measured against
    // the resting position, not a leftover knob displacement.
    if (this.heroElement) this.heroElement.style.transform = 'translate(0, 0)';
    this.centreJoystickOnPlayer();
    this.joystickElement.classList.add(this.activeClass);

    const moveEvent = this.createMoveEventHandler(x, y, rect);
    const endEvent = () => this.endInteraction();
    this.activeMoveHandler = moveEvent;
    this.activeEndHandler = endEvent;

    document.addEventListener('mousemove', moveEvent);
    document.addEventListener('mouseup', endEvent);
    document.addEventListener('touchmove', moveEvent);
    document.addEventListener('touchend', endEvent);
  }

  private getEventCoordinates(event: MouseEvent | TouchEvent): { x: number; y: number; rect: DOMRect } {
    const rect = this.navigatorElement.getBoundingClientRect();
    const clientX = event instanceof MouseEvent ? event.clientX : event.touches[0].clientX;
    const clientY = event instanceof MouseEvent ? event.clientY : event.touches[0].clientY;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    return { x, y, rect };
  }

  private setJoystickPosition(x: number, y: number): void {
    const translateX = x - this.joystickElement.offsetWidth / 2;
    const translateY = y - this.joystickElement.offsetHeight / 2;
    this.joystickElement.style.transform = `translate(${translateX}px, ${translateY}px)`;
  }

  /** Centres the joystick on the hero dot's current viewport position. The hero
   * stays at a fixed on-screen spot (it doesn't move as the camera pans), so this
   * is stable across navigation and works on both the desktop frame and mobile. */
  private centreJoystickOnPlayer(): void {
    if (!this.heroElement) return;
    const rect = this.heroElement.getBoundingClientRect();
    this.setJoystickPosition(rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  private createMoveEventHandler(x: number, y: number, rect: DOMRect): (event: MouseEvent | TouchEvent) => void {
    return (moveEvent: MouseEvent | TouchEvent) => {
      this.lastX = x;
      this.lastY = y;
      this.lastMoveEvent = moveEvent;
      const moveClientX = moveEvent instanceof MouseEvent ? moveEvent.clientX : moveEvent.touches[0].clientX;
      const moveClientY = moveEvent instanceof MouseEvent ? moveEvent.clientY : moveEvent.touches[0].clientY;
      this.handleMoveEvent(moveClientX, moveClientY, x, y, rect);
    };
  }

  private handleMoveEvent(moveClientX: number, moveClientY: number, x: number, y: number, rect: DOMRect): void {
    if (!this.heroElement) return;
    let moveX = moveClientX - rect.left - x;
    let moveY = moveClientY - rect.top - y;
    const distance = Math.sqrt(moveX * moveX + moveY * moveY);
    const maxDistance = (this.joystickElement.offsetWidth - this.heroElement.offsetWidth) / 2;

    let atRim = false;
    let angle = 0;
    if (distance > maxDistance) {
      angle = Math.atan2(moveY, moveX);
      moveX = Math.cos(angle) * maxDistance;
      moveY = Math.sin(angle) * maxDistance;
      atRim = true;
    }

    // The hero dot itself is the knob: it slides within the circle to show the
    // drag, instead of a separate marker.
    this.heroElement.style.transform = `translate(${moveX}px, ${moveY}px)`;

    // Held at the rim toward a valid exit → charge; anything else disarms. Once a
    // move has committed for this press, stay disarmed until navigation ends.
    if (atRim && !this.hasNavigated) {
      this.updateCharge(this.resolveDestination(angle * (180 / Math.PI)));
    } else {
      this.cancelCharge();
    }
  }

  /**
   * Arms or sustains the 1s charge toward `destination`. A null destination (no
   * exit within the angular threshold) disarms. A *changed* destination restarts
   * the charge so the ring fill always reflects the move about to commit.
   */
  private updateCharge(destination: POI_ID | null): void {
    if (!destination) {
      this.cancelCharge();
      return;
    }
    if (destination === this.chargingDestination) return;
    const heading = this.directions.find((direction) => direction.destination === destination)?.angle ?? 0;
    this.startCharge(destination, heading);
  }

  private startCharge(destination: POI_ID, headingDegrees: number): void {
    this.cancelCharge();
    this.chargingDestination = destination;
    // Begin the ring fill at the heading so it emanates from the direction of travel.
    this.joystickElement.style.setProperty('--charge-angle', `${headingDegrees}deg`);
    // Re-add the class after a reflow so the ring fill replays from empty.
    void this.joystickElement.offsetWidth;
    this.joystickElement.classList.add(this.chargingClass);
    this.chargeTimer = setTimeout(() => this.commitCharge(), this.chargeDurationMs);
  }

  private commitCharge(): void {
    const destination = this.chargingDestination;
    this.cancelCharge();
    if (!destination) return;

    this.navigate(destination);
    this.hasNavigated = true;

    // Horseback (future): the finger stays live and re-charges on arrival.
    if (this.continuousMovement) return;

    // The hero is on its way, so the joystick goes away now rather than on
    // arrival: the player can press again and charge the next move while the
    // camera is still panning. The committing finger is spent — its listeners
    // are detached, so holding it does nothing until it is released and pressed
    // again.
    this.endInteraction();
    // Aim at the exits of the POI being travelled to: a charge started during
    // travel must resolve against where the hero is going, not where it left.
    this.aimAtDestination(destination);
  }

  /**
   * Points the joystick at `destination`'s exits for the duration of the
   * travel. A portal destination is the exception: it swaps the map mid-flight,
   * so the exits it lists are unreachable by the time the hero lands. Offering
   * none is better than offering wrong ones — the player would spend a full
   * charge on a move the Navigator then refuses. navigationEnd repopulates them
   * from the POI actually landed on.
   */
  private aimAtDestination(destination: POI_ID): void {
    if (getPOIFrom(destination)?.portal) {
      this.directions = [];
      return;
    }
    this.setAvailableDirections(destination);
  }

  private cancelCharge(): void {
    if (this.chargeTimer !== null) {
      clearTimeout(this.chargeTimer);
      this.chargeTimer = null;
    }
    this.chargingDestination = null;
    this.joystickElement.classList.remove(this.chargingClass);
  }

  /**
   * Maps a heading (degrees, atan2 convention: 0 = right, 90 = down) to the
   * best-matching available destination. Shared by the drag joystick, the
   * clickable direction arrows and the backdrop clicks so direction→POI
   * resolution lives in exactly one place. Returns null if no exit is within
   * the angular threshold.
   */
  private resolveDestination(angleDegrees: number): POI_ID | null {
    let best: POI_ID | null = null;
    let bestDiff = this.directionThreshold;

    this.directions.forEach((direction) => {
      const diff = this.angleDelta(angleDegrees, direction.angle);
      if (diff <= bestDiff) {
        bestDiff = diff;
        best = direction.destination;
      }
    });

    return best;
  }

  /** Smallest absolute difference between two angles in degrees (handles wrap). */
  private angleDelta(a: number, b: number): number {
    const diff = Math.abs(a - b) % 360;
    return diff > 180 ? 360 - diff : diff;
  }

  /** Detaches the current press's drag listeners and clears their references.
   * Safe to call when no interaction is active. */
  private removeInteractionListeners(): void {
    if (this.activeMoveHandler) {
      document.removeEventListener('mousemove', this.activeMoveHandler);
      document.removeEventListener('touchmove', this.activeMoveHandler);
    }
    if (this.activeEndHandler) {
      document.removeEventListener('mouseup', this.activeEndHandler);
      document.removeEventListener('touchend', this.activeEndHandler);
    }
    this.activeMoveHandler = null;
    this.activeEndHandler = null;
  }

  /** Tears the joystick down: cancels any charge, fades out the ring/knob
   * (removes is-active), snaps the hero dot back to centre and detaches the
   * drag listeners. Idempotent — safe on release, navigation end or a stray
   * call. */
  private endInteraction(): void {
    this.isMousePressed = false;
    this.cancelCharge();
    this.joystickElement.classList.remove(this.activeClass);
    // Snap the hero dot back to the player's true centre.
    if (this.heroElement) this.heroElement.style.transform = 'translate(0, 0)';
    this.removeInteractionListeners();
  }

  private onNavigationEnd(poi: POI_ID): void {
    this.setAvailableDirections(poi);

    // Horseback (future): if the joystick is still held toward a valid exit when
    // the move finishes, re-evaluate the drag and keep moving.
    if (this.continuousMovement) {
      this.hasNavigated = false;
      if (this.isMousePressed && this.lastMoveEvent) {
        const rect = this.navigatorElement.getBoundingClientRect();
        const moveEvent = this.createMoveEventHandler(this.lastX, this.lastY, rect);
        moveEvent(this.lastMoveEvent);
      }
      return;
    }

    // Default: nothing to tear down here — the joystick was dismissed the
    // moment the move committed. Arrival must leave a press started during
    // travel alone, or it would cancel a charge already under way. The refresh
    // above is still worth doing: it corrects the optimistic aim taken at
    // commit (portal transitions land somewhere else entirely).
  }

  private setAvailableDirections(currentPoi: POI_ID): void {
    this.hasNavigated = false;
    this.directions = [];
    const poi = getPOIFrom(currentPoi);
    const poiElement = document.getElementById(currentPoi);
    poi?.destination?.forEach((destination) => {
      const destinationElement = document.getElementById(destination);

      if (poiElement && destinationElement) {
        const { angle } = this.calculateAngle(poiElement, destinationElement);
        this.directions.push({ destination, angle });
      }
    });
  }

  private calculateAngle(poiElement: HTMLElement, destinationElement: HTMLElement): { angle: number } {
    const poiRect = poiElement.getBoundingClientRect();
    const destinationRect = destinationElement.getBoundingClientRect();

    const poiCenterX = poiRect.left + poiRect.width / 2;
    const poiCenterY = poiRect.top + poiRect.height / 2;
    const destinationCenterX = destinationRect.left + destinationRect.width / 2;
    const destinationCenterY = destinationRect.top + destinationRect.height / 2;

    const dx = destinationCenterX - poiCenterX;
    const dy = destinationCenterY - poiCenterY;

    const angle = Math.atan2(dy, dx) * (180 / Math.PI);
    return { angle };
  }

  private navigate(destination: POI_ID): void {
    eventBus.emit(NavigatorEventBus.navigationStart, destination);
  }
}
